import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Typography, Grid, MenuItem, FormControl, Select, TextField, InputAdornment, IconButton, Avatar } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Chart } from 'react-google-charts';
import { computeSummary } from './scoring';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const REFRESH_INTERVAL = 5 * 60 * 1000;

const CELEBRITIES = [
  'Justin Baldoni',
  'Blake Lively',
  'Ryan Reynolds',
  'Taylor Swift',
  'Kim Kardashian',
  'Donald Trump',
  'Beyonce',
  'Bad Bunny',
  'Ariana Grande',
  'Jennifer Lopez',
  'Keke Palmer',
  'Mariah Carey',
  'Doja Cat',
  'Cardi B',
  'Kendrick Lamar',
  'Rihanna',
  'Selena Gomez',
  'BTS',
  '50 Cent',
  'Ice Spice',
  'Miley Cyrus',
  'Hilary Duff',
];

const QUOTES = {
  negative: [
    "Don't let the players stop you from playing the game",
    "Haters are just confused admirers",
    "I'd rather be hated for who I am than loved for who I'm not — Kurt Cobain",
    "Living rent free in their heads since day one",
    "If they don't know you personally, don't take it personal — Khaled",
    "I'm too glam to give a damn",
    "Haters gonna hate, potatoes gonna potate",
    "She remembered who she was and the game changed",
    "I'm a survivor, I'm gonna make it — Destiny's Child",
    "The best revenge is massive success — Frank Sinatra",
    "Not everyone deserves a seat at your table",
    "Talk about me all you want, you're making me famous",
  ],
  positive: [
    "I didn't come to play, I came to slay — Beyonce",
    "She's beauty, she's grace, she'll roast you to your face",
    "Main character energy only",
    "Be the energy you want to attract",
    "Why be moody when you can shake your booty",
    "The people have spoken and they said SLAY",
    "When you're this iconic, the love writes itself",
    "That's on being unproblematic and thriving",
    "The vibes are immaculate",
    "Mother is mothering",
    "Unbothered. Moisturized. In my lane. Thriving.",
  ],
  neutral: [
    "The internet is undefeated",
    "Not my circus, not my monkeys",
    "Don't be pressed, be blessed",
    "Talk to me nice or don't talk to me at all",
    "You can't sit with us — Mean Girls",
    "The tea is room temperature today",
    "No drama, just vibes",
    "Somewhere between iconic and unhinged",
    "The algorithm giveth and the algorithm taketh away",
    "Low-key legendary, high-key unbothered",
  ],
};

function pickQuote(sentiment) {
  const list = QUOTES[sentiment] || QUOTES.neutral;
  return list[Math.floor(Math.random() * list.length)];
}

const theme = createTheme({
  palette: {
    primary: { main: '#ec4899' },
    secondary: { main: '#8b5cf6' },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', -apple-system, sans-serif",
  },
});

const COLORS = {
  positive: '#4ade80',
  negative: '#fb7185',
  neutral: '#cbd5e1',
  reddit: '#fda4af',
  gnews: '#93c5fd',
};

const LOADING_MESSAGES = [
  'Spilling the tea...',
  'Scanning the gossip...',
  'Reading the comments...',
  'Checking Reddit...',
  'Gathering the receipts...',
  'Digging through the drama...',
  'Consulting the tabloids...',
  'Hot goss incoming...',
  'Sipping and scrolling...',
  'The tea is brewing...',
];

function buildTimelineData(results) {
  const byDate = {};
  for (const r of results) {
    const day = new Date(r.posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!byDate[day]) byDate[day] = { positive: 0, negative: 0, neutral: 0 };
    byDate[day][r.sentiment.label]++;
  }
  const entries = Object.entries(byDate);
  if (entries.length === 0) return null;
  return [
    ['Date', 'Positive', 'Neutral', 'Negative'],
    ...entries.reverse().map(([date, counts]) => [date, counts.positive, counts.neutral, counts.negative]),
  ];
}

function buildPlatformData(results) {
  const platforms = {};
  for (const r of results) {
    const p = r.platform === 'reddit' ? 'Reddit' : 'News';
    if (!platforms[p]) platforms[p] = { positive: 0, negative: 0, neutral: 0 };
    platforms[p][r.sentiment.label]++;
  }
  const entries = Object.entries(platforms);
  if (entries.length === 0) return null;
  return [
    ['Platform', 'Positive', 'Neutral', 'Negative'],
    ...entries.map(([name, counts]) => [name, counts.positive, counts.neutral, counts.negative]),
  ];
}

function buildSourceData(results) {
  const sources = {};
  for (const r of results) {
    const name = r.source || r.platform;
    sources[name] = (sources[name] || 0) + 1;
  }
  const sorted = Object.entries(sources).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (sorted.length === 0) return null;
  return [['Source', 'Mentions'], ...sorted];
}

function mergeResults(existing, incoming) {
  const seen = new Set(existing.map(r => r.url));
  const merged = [...existing];
  for (const r of incoming) {
    if (!seen.has(r.url)) {
      seen.add(r.url);
      merged.push(r);
    }
  }
  merged.sort((a, b) => new Date(b.posted_at) - new Date(a.posted_at));
  return merged;
}

function App() {
  const [celebrity, setCelebrity] = useState('Justin Baldoni');
  const [searchInput, setSearchInput] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [wikiImage, setWikiImage] = useState(null);
  const [platform, setPlatform] = useState('all');
  const [loadingMsg, setLoadingMsg] = useState(LOADING_MESSAGES[0]);
  const intervalRef = useRef(null);
  const accumulatedRef = useRef({});

  const fetchSearch = useCallback(async (celeb, plat, isRefresh) => {
    if (!isRefresh) {
      setLoading(true);
      setError(null);
    } else {
      setRefreshing(true);
    }

    try {
      const res = await fetch(`${API_URL}/api/search?q=${encodeURIComponent(celeb)}&platforms=${plat}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to fetch data');
      }
      const freshData = await res.json();

      const key = `${celeb}__${plat}`;
      const prev = accumulatedRef.current[key] || [];
      const merged = mergeResults(prev, freshData.results || []);
      accumulatedRef.current[key] = merged;

      const summary = computeSummary(merged);
      setData({ subject: freshData.subject, fetched_at: freshData.fetched_at, summary, results: merged });
    } catch (err) {
      if (!isRefresh) setError(err.message || 'Failed to fetch data');
    } finally {
      if (!isRefresh) setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    accumulatedRef.current = {};
    setWikiImage(null);

    const run = async () => {
      await fetchSearch(celebrity, platform, false);
      try {
        const wikiRes = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(celebrity)}`);
        if (!cancelled && wikiRes.ok) {
          const wiki = await wikiRes.json();
          if (wiki.thumbnail?.source) setWikiImage(wiki.thumbnail.source);
        }
      } catch {}
    };
    run();

    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      if (!cancelled) fetchSearch(celebrity, platform, true);
    }, REFRESH_INTERVAL);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [celebrity, platform, fetchSearch]);

  useEffect(() => {
    if (!loading) return;
    let i = 0;
    setLoadingMsg(LOADING_MESSAGES[0]);
    const timer = setInterval(() => {
      i = (i + 1) % LOADING_MESSAGES.length;
      setLoadingMsg(LOADING_MESSAGES[i]);
    }, 2200);
    return () => clearInterval(timer);
  }, [loading]);

  const resultQuote = useMemo(() => {
    if (!data) return '';
    if (data.results.length === 0) return pickQuote('negative');
    return pickQuote(data.summary.overall_sentiment);
  }, [data]);

  const handleSelectChange = (event) => setCelebrity(event.target.value);

  const handleSearch = () => {
    const trimmed = searchInput.trim();
    if (trimmed) {
      setCelebrity(trimmed);
      setSearchInput('');
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') handleSearch();
  };

  const sentimentChartData = data ? [
    ['Sentiment', 'Count'],
    ['Positive', data.summary.counts.positive],
    ['Negative', data.summary.counts.negative],
    ['Neutral', data.summary.counts.neutral],
  ] : null;

  const timelineData = useMemo(() => data ? buildTimelineData(data.results) : null, [data]);
  const platformData = useMemo(() => data ? buildPlatformData(data.results) : null, [data]);
  const sourceData = useMemo(() => data ? buildSourceData(data.results) : null, [data]);

  const sentimentRatio = data ? (() => {
    const total = data.summary.counts.positive + data.summary.counts.neutral + data.summary.counts.negative;
    if (total === 0) return 50;
    return Math.round((data.summary.counts.positive / total) * 100);
  })() : 50;

  return (
    <ThemeProvider theme={theme}>
      <div className="app-root">
        <div className="hero">
          <SparkleField />
          <Typography className="hero-title" variant="h4" component="h1">
            Celebrity "Haters" Analysis
          </Typography>
          <Typography className="hero-subtitle">
            Real-time sentiment analysis from news and Reddit
          </Typography>

          <div className="search-area">
            <FormControl sx={{ minWidth: 220 }}>
              <Select
                value={celebrity}
                onChange={handleSelectChange}
                sx={{ borderRadius: '12px', fontWeight: 500 }}
              >
                {CELEBRITIES.map((name) => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
                {!CELEBRITIES.includes(celebrity) && (
                  <MenuItem value={celebrity}>{celebrity}</MenuItem>
                )}
              </Select>
            </FormControl>
            <TextField
              placeholder="Or search any name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              size="small"
              sx={{ width: 260 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSearch} size="small" aria-label="search">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className="source-toggle">
              {[
                { value: 'all', label: 'All Sources' },
                { value: 'reddit', label: 'Reddit' },
                { value: 'gnews', label: 'News' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  className={`toggle-btn${platform === opt.value ? ' active' : ''}`}
                  onClick={() => setPlatform(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <div className="loading-hearts">
              {['💖', '💗', '💕', '💘', '💝'].map((h, i) => (
                <span key={i} className="loading-heart" style={{ '--i': i }}>{h}</span>
              ))}
            </div>
            <div className="loading-sparkle-ring">
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="ring-dot" style={{ '--dot-i': i }} />
              ))}
            </div>
            <Typography className="loading-message">{loadingMsg}</Typography>
            <Typography sx={{ color: '#d4a0c0', fontSize: '0.75rem', mt: 1 }}>
              Analyzing {celebrity}
            </Typography>
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: '40px 24px' }}>
            <div className="error-container">
              <Typography sx={{ color: '#ef4444', fontWeight: 600, mb: 1 }}>
                Something went wrong
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                {error}
              </Typography>
            </div>
          </div>
        )}

        {data && !loading && !error && (
          <>
            <div className="profile-section">
              {wikiImage && (
                <Avatar
                  src={wikiImage}
                  alt={data.subject}
                  className="profile-avatar"
                  sx={{ width: 100, height: 100 }}
                />
              )}
              <Typography className="profile-name">{data.subject}</Typography>
              <div className="profile-meta">
                <span className={`meta-chip sentiment-${data.summary.overall_sentiment}`}>
                  {data.summary.overall_sentiment === 'positive' ? '+' : data.summary.overall_sentiment === 'negative' ? '-' : '~'}{' '}
                  {data.summary.overall_sentiment}
                </span>
                <span className="meta-chip buzz">
                  <TrendingUpIcon sx={{ fontSize: 14 }} />
                  Buzz {data.summary.buzz_score}/100
                </span>
                <span className="meta-chip buzz" style={{ fontSize: '0.7rem' }}>
                  {data.results.length} mentions
                  {refreshing && <span className="refresh-dot" />}
                </span>
              </div>
              {resultQuote && (
                <Typography className="result-quote">
                  "{resultQuote}"
                </Typography>
              )}
            </div>

            <div className="dashboard">
              {/* Row 1: Core metrics */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <div className="card">
                    <Typography className="card-title">Sentiment Breakdown</Typography>
                    <Typography className="card-description">
                      {data.summary.counts.positive + data.summary.counts.neutral + data.summary.counts.negative} mentions analyzed
                    </Typography>
                    {sentimentChartData && (
                      <Chart
                        chartType="PieChart"
                        data={sentimentChartData}
                        width="100%"
                        height="220px"
                        options={{
                          backgroundColor: 'transparent',
                          legend: { position: 'bottom', textStyle: { fontSize: 12, color: '#64748b' } },
                          colors: [COLORS.positive, COLORS.negative, COLORS.neutral],
                          pieHole: 0.45,
                          pieSliceTextStyle: { fontSize: 13 },
                          chartArea: { width: '90%', height: '75%' },
                        }}
                      />
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <div className="card">
                    <Typography className="card-title">Recent Mentions</Typography>
                    <Typography className="card-description">
                      Latest from news &amp; Reddit
                    </Typography>
                    <MentionsCard mentions={data.results} />
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <div className="card">
                    <Typography className="card-title">Buzz Score</Typography>
                    <Typography className="card-description">
                      Overall media attention level
                    </Typography>
                    <div className="buzz-display">
                      <Typography className="buzz-number">{data.summary.buzz_score}</Typography>
                      <Typography className="buzz-label">out of 100</Typography>
                      <Typography className="buzz-explainer">
                        How much the internet is talking about this person right now — based on how many articles and Reddit posts exist and how many upvotes those posts are getting.
                      </Typography>
                      <div className="buzz-bar-track">
                        <div className="buzz-bar-fill" style={{ width: `${data.summary.buzz_score}%` }} />
                      </div>
                    </div>
                  </div>
                </Grid>
              </Grid>

              {/* Row 2: Timeline + Gauge */}
              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={8}>
                  <div className="card">
                    <Typography className="card-title">Sentiment Timeline</Typography>
                    <Typography className="card-description">
                      How sentiment shifts across recent mentions
                    </Typography>
                    {timelineData ? (
                      <Chart
                        chartType="AreaChart"
                        data={timelineData}
                        width="100%"
                        height="260px"
                        options={{
                          backgroundColor: 'transparent',
                          legend: { position: 'bottom', textStyle: { fontSize: 12, color: '#64748b' } },
                          colors: [COLORS.positive, COLORS.neutral, COLORS.negative],
                          curveType: 'function',
                          areaOpacity: 0.15,
                          lineWidth: 2.5,
                          pointSize: 5,
                          pointShape: 'circle',
                          chartArea: { width: '85%', height: '68%' },
                          hAxis: { textStyle: { fontSize: 11, color: '#94a3b8' }, gridlines: { color: 'transparent' } },
                          vAxis: { textStyle: { fontSize: 11, color: '#94a3b8' }, gridlines: { color: '#f1f5f9' }, minValue: 0, format: '#' },
                          isStacked: false,
                          focusTarget: 'category',
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', py: 6 }}>
                        Not enough data for timeline
                      </Typography>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} md={4}>
                  <div className="card">
                    <Typography className="card-title">Positivity Meter</Typography>
                    <Typography className="card-description">
                      Ratio of positive mentions
                    </Typography>
                    <SentimentGauge ratio={sentimentRatio} counts={data.summary.counts} />
                  </div>
                </Grid>
              </Grid>

              {/* Row 3: Platform comparison + Top sources */}
              <Grid container spacing={3} sx={{ mt: 0 }}>
                <Grid item xs={12} md={6}>
                  <div className="card">
                    <Typography className="card-title">Platform Comparison</Typography>
                    <Typography className="card-description">
                      Sentiment by source — Reddit vs News
                    </Typography>
                    {platformData ? (
                      <Chart
                        chartType="BarChart"
                        data={platformData}
                        width="100%"
                        height="200px"
                        options={{
                          backgroundColor: 'transparent',
                          legend: { position: 'bottom', textStyle: { fontSize: 12, color: '#64748b' } },
                          colors: [COLORS.positive, COLORS.neutral, COLORS.negative],
                          chartArea: { width: '70%', height: '65%' },
                          hAxis: { textStyle: { fontSize: 11, color: '#94a3b8' }, gridlines: { color: '#f1f5f9' }, minValue: 0, format: '#' },
                          vAxis: { textStyle: { fontSize: 12, color: '#475569' } },
                          isStacked: true,
                          bar: { groupWidth: '50%' },
                          annotations: { stemLength: 0 },
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', py: 6 }}>
                        Not enough data
                      </Typography>
                    )}
                  </div>
                </Grid>

                <Grid item xs={12} md={6}>
                  <div className="card">
                    <Typography className="card-title">Top Sources</Typography>
                    <Typography className="card-description">
                      Where the conversation is happening
                    </Typography>
                    {sourceData ? (
                      <Chart
                        chartType="BarChart"
                        data={sourceData}
                        width="100%"
                        height="200px"
                        options={{
                          backgroundColor: 'transparent',
                          legend: { position: 'none' },
                          colors: ['#c4b5fd'],
                          chartArea: { width: '55%', height: '75%' },
                          hAxis: { textStyle: { fontSize: 11, color: '#94a3b8' }, gridlines: { color: '#f1f5f9' }, minValue: 0, format: '#' },
                          vAxis: { textStyle: { fontSize: 11, color: '#475569' } },
                          bar: { groupWidth: '55%' },
                        }}
                      />
                    ) : (
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', py: 6 }}>
                        Not enough data
                      </Typography>
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>
          </>
        )}

        <footer className="site-footer">
          <p>This tool aggregates publicly available data for educational and analytical purposes. All content belongs to its original sources.</p>
          <p>Photos via Wikipedia (Creative Commons). News data via GNews. Reddit data via Reddit API.</p>
        </footer>
      </div>
    </ThemeProvider>
  );
}

const SentimentGauge = ({ ratio, counts }) => {
  const rotation = (ratio / 100) * 180 - 90;
  return (
    <div className="gauge-container">
      <svg viewBox="0 0 200 120" className="gauge-svg">
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.negative} />
            <stop offset="50%" stopColor={COLORS.neutral} />
            <stop offset="100%" stopColor={COLORS.positive} />
          </linearGradient>
        </defs>
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="#f1f5f9"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="url(#gaugeGrad)"
          strokeWidth="14"
          strokeLinecap="round"
        />
        <line
          x1="100"
          y1="100"
          x2="100"
          y2="35"
          stroke="#1e293b"
          strokeWidth="2.5"
          strokeLinecap="round"
          transform={`rotate(${rotation}, 100, 100)`}
        />
        <circle cx="100" cy="100" r="5" fill="#1e293b" />
        <text x="20" y="118" fontSize="10" fill="#94a3b8" textAnchor="start">negative</text>
        <text x="180" y="118" fontSize="10" fill="#94a3b8" textAnchor="end">positive</text>
      </svg>
      <div className="gauge-value">{ratio}% positive</div>
      <div className="gauge-counts">
        <span className="gauge-count positive">{counts.positive} pos</span>
        <span className="gauge-count neutral">{counts.neutral} neu</span>
        <span className="gauge-count negative">{counts.negative} neg</span>
      </div>
    </div>
  );
};

const MentionsCard = ({ mentions }) => (
  <div className="mentions-scroll">
    {mentions.length === 0 ? (
      <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', py: 4 }}>
        No mentions found.
      </Typography>
    ) : (
      mentions.map((mention, index) => (
        <div key={index} className="mention-item">
          <Typography className="mention-title">
            <a href={mention.url} target="_blank" rel="noopener noreferrer">
              {mention.title}
            </a>
          </Typography>
          <div className="mention-meta">
            <span className={`mention-tag platform-${mention.platform}`}>
              {mention.source}
            </span>
            <span className={`mention-tag sent-${mention.sentiment.label}`}>
              {mention.sentiment.label}
            </span>
            <span className="mention-date">
              {new Date(mention.posted_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      ))
    )}
  </div>
);

const HEARTS = ['💖', '💗', '💕', '💘', '💝', '♡', '❤️', '💜', '💖'];

const SparkleField = () => (
  <div className="sparkle-field">
    {HEARTS.map((h, i) => (
      <span
        key={`h${i}`}
        className="float-heart"
        style={{
          left: `${6 + i * 11}%`,
          '--duration': `${5 + (i % 4) * 2}s`,
          '--delay': `${i * 0.7}s`,
          '--size': `${14 + (i % 3) * 6}px`,
        }}
      >
        {h}
      </span>
    ))}
    {Array.from({ length: 18 }).map((_, i) => (
      <span
        key={`s${i}`}
        className="sparkle"
        style={{
          left: `${3 + (i * 37) % 94}%`,
          top: `${5 + (i * 23) % 80}%`,
          '--spark-size': `${2 + (i % 3) * 2}px`,
          '--twinkle-dur': `${1.5 + (i % 5) * 0.6}s`,
          '--twinkle-delay': `${i * 0.3}s`,
        }}
      />
    ))}
    {Array.from({ length: 8 }).map((_, i) => (
      <span
        key={`st${i}`}
        className="sparkle-star"
        style={{
          left: `${8 + (i * 43) % 84}%`,
          top: `${10 + (i * 31) % 70}%`,
          '--star-size': `${10 + (i % 3) * 4}px`,
          '--star-dur': `${2.5 + (i % 4) * 0.8}s`,
          '--star-delay': `${i * 0.5 + 0.2}s`,
        }}
      />
    ))}
  </div>
);

export default App;
